import { useState, useEffect } from "react";
import { Utensils, BusFront, Sparkles, ArrowRight, Clock, CalendarDays } from "lucide-react";
import { api } from "@/lib/api";
import KitchenTickets from "./KitchenTickets";

const todayStr = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const STATUS = {
  pending: "bg-primary text-foreground",
  confirmed: "bg-secondary text-secondary-foreground",
  cancelled: "bg-muted text-foreground/40",
};

function BookingRow({ b, kind, onOpen }) {
  return (
    <button
      onClick={onOpen}
      data-testid={`today-booking-${b.id}`}
      className="w-full text-left flex items-center gap-3 border-2 border-foreground/15 rounded-xl p-3 hover:border-foreground hover:bg-muted transition-colors group"
    >
      <span className="shrink-0">{kind === "table" ? <Utensils size={16} /> : <BusFront size={16} />}</span>
      <div className="flex-1 min-w-0">
        <p className="font-head font-bold leading-tight truncate">{b.name}</p>
        <p className="font-mono-plex text-xs text-foreground/60">
          {b.time}{b.party_size ? ` · ${b.party_size} ${b.party_size === 1 ? "guest" : "guests"}` : ""}{b.phone ? ` · ${b.phone}` : ""}
        </p>
      </div>
      <span className={`font-head font-bold text-[10px] px-2.5 py-1 rounded-full shrink-0 ${STATUS[b.status] || "bg-muted"}`}>{(b.status || "").toUpperCase()}</span>
      <ArrowRight size={16} className="shrink-0 text-foreground/30 group-hover:text-foreground group-hover:translate-x-0.5 transition-all" />
    </button>
  );
}

export default function AdminHome({ tableB = [], vanB = [], onOpenBooking, canViewBookings, canBookings }) {
  const [specials, setSpecials] = useState([]);
  useEffect(() => { api.get("/content").then(({ data }) => setSpecials(data.specials || [])).catch(() => {}); }, []);

  const today = todayStr();
  const byTime = (a, b) => (a.time || "").localeCompare(b.time || "");
  const tToday = tableB.filter((b) => b.date === today && b.status !== "cancelled").sort(byTime);
  const vToday = vanB.filter((b) => b.date === today && b.status !== "cancelled").sort(byTime);
  const totalToday = tToday.length + vToday.length;

  const niceDate = new Date().toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="space-y-10" data-testid="admin-dashboard-home">
      <div className="flex items-center gap-2">
        <CalendarDays size={20} className="text-secondary" />
        <h2 className="font-head font-black text-2xl">Today</h2>
        <span className="font-mono-plex text-sm text-foreground/50">{niceDate}</span>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 items-start">
        {/* Today's bookings */}
        {canViewBookings && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h3 className="font-head font-extrabold text-lg">Today's bookings</h3>
              <span className="font-mono-plex text-sm bg-primary rounded-full px-2.5 py-0.5">{totalToday}</span>
            </div>
            {totalToday === 0 ? (
              <p className="font-mono-plex text-sm text-foreground/50 border-2 border-dashed border-foreground/30 rounded-xl p-6 text-center" data-testid="no-today-bookings">Nothing booked for today yet.</p>
            ) : (
              <div className="space-y-2">
                {tToday.map((b) => <BookingRow key={b.id} b={b} kind="table" onOpen={() => onOpenBooking("table")} />)}
                {vToday.map((b) => <BookingRow key={b.id} b={b} kind="van" onOpen={() => onOpenBooking("van")} />)}
              </div>
            )}
            <p className="font-mono-plex text-xs text-foreground/50">Tap a booking to open it in Bookings and confirm, change or cancel.</p>
          </div>
        )}

        {/* Today's specials */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-secondary" />
            <h3 className="font-head font-extrabold text-lg">Today's specials</h3>
          </div>
          {specials.length === 0 ? (
            <p className="font-mono-plex text-sm text-foreground/50 border-2 border-dashed border-foreground/30 rounded-xl p-6 text-center">No specials set. Add them in Specials &amp; Story.</p>
          ) : (
            <ul className="space-y-2" data-testid="today-specials">
              {specials.map((s, i) => (
                <li key={i} className="flex items-start gap-3 border-2 border-foreground/15 rounded-xl p-3 font-semibold">
                  <span className="w-2 h-2 rounded-full bg-secondary shrink-0 mt-2" /> <span>{s}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Takeaway queue (with reprint) */}
      {canBookings && (
        <div className="border-t-2 border-foreground/10 pt-8">
          <KitchenTickets />
        </div>
      )}
    </div>
  );
}
